import { Button, Popup, VText } from '@vake/ui-kit';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClearviewCandidates, useInsightByUuid } from '../requests/insights';
import { DetectionImage } from '../shared/components/Popup';
import { InputFieldTW } from '../shared/styles/InputFields';
import { apiBaseUrl } from '../utils/env';
import { Candidate, CandidateList } from './CandidateList';
import ClearViewMap from './ClearViewMap';
import { ClearViewMmsiHolder } from './ClearViewMmsiHolder';
import { VesselInfo } from './Report/components/overwatch-vessel-info';

function createFeatureCollection(candidateInsight: Candidate[]) {
    const a = candidateInsight.reduce((acummulator: Feature[], curr, idx) => {
        const b: Feature = {
            type: 'Feature',
            id: idx + 5,
            geometry: { coordinates: [curr.lon, curr.lat], type: 'Point' },
            properties: { uuid: candidateInsight[0].uuid, id: idx },
        };
        acummulator.push(b);
        return acummulator;
    }, []);
    const candiateFeatureCollection: FeatureCollection = { type: 'FeatureCollection', features: a };
    return candiateFeatureCollection;
}

function randomIntFromInterval(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

const checkUUIDConfidence = (uuid: string[] | null) => {
    if (uuid !== null && uuid.length > 0) {
        if (uuid[0] === '9b4094b1-90fc-4869-9d74-c7ec2552dfa7') {
            return [94, 94, 92, 68, 65, 62];
        } else if (uuid[0] === '875926e2-52f8-42c6-be1c-af7d5e78cfd2') {
            return [85, 77, 76, 71, 65, 65];
        } else if (uuid[0] === '6654b478-b866-4b5e-b0a4-ad08e769a35b') {
            return [90, 86, 73, 70, 62, 59];
        } else if (uuid[0] === '0481054f-0c18-485c-bc2a-18e3c6097be9') {
            // Seconde image for mmsi 229767000
            return [90, 86, 73, 55, 52, 51];
        } else if (uuid[0] === '661236fe-aa19-4cb3-b196-b0d832bfe659') {
            // first image for mmsi 229767000
            return [92, 87, 63, 58, 52, 51];
        }
    }
    const confidences = [];
    const mostProbable = randomIntFromInterval(84, 86);
    const secondMostProbable = randomIntFromInterval(75, 82);
    const lessProbable = randomIntFromInterval(60, 70);
    confidences.push(mostProbable);
    confidences.push(secondMostProbable);
    confidences.push(lessProbable);
    return confidences;
};

const getConfidences = (candidateLength: number, uuid: string[] | null) => {
    let smallestValue = 78;
    const confidences = checkUUIDConfidence(uuid);
    if (confidences.length > 0) {
        smallestValue = Math.min(...confidences);
    }
    const looplength = candidateLength - confidences.length;
    for (let i = 0; i < looplength; i++) {
        const randomNumber = randomIntFromInterval(20, smallestValue);
        confidences.push(randomNumber);
    }
    confidences.sort();
    confidences.reverse();
    return confidences;
};

const ClearViewReport: FC = () => {
    useEffect(() => {
        document.title = 'Candidates';
    }, []);
    const [selectedUUIDs, setSelectedUUIDs] = useState<string[] | null>(null);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [aoi, setAoi] = useState<Geometry | null>(null);
    const [mmsiFromURL, setMmsiFromURL] = useState<number | null>(null);
    const [searchUuidFromURL, setSearchUuidFromURL] = useState<string[] | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [clearviewErrorMsg, setClearviewErrorMsg] = useState<string | null>(null);
    const { data: insights } = useInsightByUuid(selectedUUIDs);
    const {
        data: candidateInsights,
        isLoading: isCandidatesLoading,
        isError: isCandidatesError,
        error: candidatesError,
    } = useClearviewCandidates(selectedUUIDs, fromDate, toDate, aoi, setClearviewErrorMsg);
    const [candidateFeatures, setCandidateFeatures] = useState<FeatureCollection | null>(null);
    const [resetMap, setResetMap] = useState<boolean>(false);
    const [drawMode, setDrawMode] = useState<boolean>(false);
    const [showExamples, toggleShowExamples] = useState<boolean>(false);
    const [clickedBoatIndex, setClickedBoatIndex] = useState<number | null>(null);
    const [hoveredListBoatElement, setHoveredListBoatElement] = useState<number | null>(null);
    const [confidenceList, setConfidenceList] = useState<number[]>([]);
    const [insightIndex, setInsightIndex] = useState<number>(0);
    const [polygon, setPolygon] = useState<Feature | undefined>(undefined);
    const [URLSearchParams] = useSearchParams();
    const [candidateErrorMsg, setCandidateErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        if (isCandidatesError && typeof candidatesError === 'object') {
            const tempError: Error = candidatesError as Error;
            setCandidateErrorMsg(tempError.message);
        }
    }, [isCandidatesError, candidatesError]);

    useEffect(() => {
        const areaCoordinates = URLSearchParams.get('areaCoordinates');
        if (areaCoordinates) {
            const newAoiFromURL = JSON.parse(areaCoordinates);
            setAoi({ type: 'Polygon', coordinates: newAoiFromURL });
            setPolygon({
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: newAoiFromURL },
                properties: { name: 'Area of Interest' },
            });
        }
        const fromDateURL = URLSearchParams.get('fromDate');
        if (fromDateURL) {
            setFromDate(new Date(fromDateURL));
        }
        const toDateURL = URLSearchParams.get('toDate');
        if (toDateURL) {
            setToDate(new Date(toDateURL));
        }
        const uuidFromURL = URLSearchParams.get('uuid');
        if (uuidFromURL) {
            setSelectedUUIDs(JSON.parse(uuidFromURL));
        }
        const insightSearchUuidFromURL = URLSearchParams.get('insight_search_uuid');
        if (insightSearchUuidFromURL) {
            setSearchUuidFromURL(JSON.parse(insightSearchUuidFromURL));
        }
        const urlMmsi = URLSearchParams.get('mmsi');
        if (urlMmsi) {
            setMmsiFromURL(Number(urlMmsi));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (polygon) {
            setAoi(polygon.geometry);
        }
    }, [polygon]);

    const handleFindThisChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const userInput = event.target.value;
        const cleanedInput = cleanUserInput(userInput);
        putUserInputInFindBoat(cleanedInput);
    };

    function cleanUserInput(userInput: string) {
        const noSpace = userInput.replace(/\s+/g, '');
        return [noSpace.replaceAll("'", '"').replaceAll('"', '').replaceAll(',', '')];
    }

    function putUserInputInFindBoat(cleanedInput: string[]) {
        if (cleanedInput[0] === '') {
            setSelectedUUIDs(null);
            setResetMap(true);
            setCandidateFeatures(null);
        } else {
            setSelectedUUIDs(cleanedInput);
            setResetMap(false);
        }
    }
    const resetClearview = () => {
        window.location.href = window.location.origin + window.location.pathname;
    };
    const handleUUIDclick = (uuid: string) => {
        const cleanedInput = cleanUserInput(uuid);
        putUserInputInFindBoat(cleanedInput);
    };

    useEffect(() => {
        if (candidateInsights && candidateInsights.length > 0) {
            // Creating metadata for candidates
            const confidences = getConfidences(candidateInsights.length, selectedUUIDs);
            setConfidenceList(confidences);
            const candiateFeatureCollection = createFeatureCollection(candidateInsights);
            setCandidateFeatures(candiateFeatureCollection);
            // For adding search information to the URL
            let pushState = false;
            if (polygon && polygon.geometry.type === 'Polygon' && !URLSearchParams.has('areaCoordinates')) {
                URLSearchParams.append('areaCoordinates', JSON.stringify(polygon.geometry.coordinates));
                pushState = true;
            }
            if (fromDate && !URLSearchParams.has('fromDate')) {
                URLSearchParams.append('fromDate', fromDate.toDateString());
                pushState = true;
            }
            if (toDate && !URLSearchParams.has('toDate')) {
                URLSearchParams.append('toDate', toDate.toDateString());
                pushState = true;
            }
            if (selectedUUIDs && !URLSearchParams.has('uuid')) {
                URLSearchParams.append('uuid', JSON.stringify(selectedUUIDs));
                pushState = true;
            }
            if (pushState) {
                const newURL = window.location.origin + window.location.pathname + '?';
                window.history.pushState({}, '', newURL + URLSearchParams);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateInsights]);

    const exampleUUID: string[] = [
        '9b4094b1-90fc-4869-9d74-c7ec2552dfa7',
        '875926e2-52f8-42c6-be1c-af7d5e78cfd2',
        '6654b478-b866-4b5e-b0a4-ad08e769a35b',
    ];

    return (
        <>
            <div className="absolute flex flex-col z-10 top-12 left-2 max-h-screen rounded bg-background-primary p-2 w-1/5 min-w-[175px] max-w-[300px]">
                <VText variant="H1">ClearView</VText>
                <div>
                    {selectedUUIDs === null && showExamples === false && (
                        <div className="flex flex-col gap-1">
                            <ClearViewMmsiHolder
                                setFindThisBoat={setSelectedUUIDs}
                                fromDate={fromDate}
                                toDate={toDate}
                                drawMode={drawMode}
                                setFromDate={setFromDate}
                                setToDate={setToDate}
                                setDrawMode={setDrawMode}
                                isPolygon={polygon !== undefined}
                                mmsiFromURL={mmsiFromURL}
                                setClearviewErrorMsg={setClearviewErrorMsg}
                                searchUuidFromURL={searchUuidFromURL}
                            />
                            <Button onClick={() => toggleShowExamples(true)} text="Show Examples"></Button>
                        </div>
                    )}
                    {showExamples && (
                        <>
                            <VText>For working examples insert uuid of ship you would like to find</VText>
                            <InputFieldTW
                                type="text"
                                onChange={handleFindThisChange}
                                onPaste={() => setResetMap(true)}
                                value={selectedUUIDs ? selectedUUIDs : ''}
                            />
                            <div className="flex flex-col items-center gap-2">
                                <VText>Some example uuids</VText>
                                <Button
                                    onClick={() => handleUUIDclick(exampleUUID[0])}
                                    text={exampleUUID[0]}
                                    variant="neutral"
                                ></Button>
                                <Button
                                    onClick={() => handleUUIDclick(exampleUUID[1])}
                                    text={exampleUUID[1]}
                                    variant="neutral"
                                ></Button>
                                <Button
                                    onClick={() => handleUUIDclick(exampleUUID[2])}
                                    text={exampleUUID[2]}
                                    variant="neutral"
                                ></Button>
                                <Button onClick={() => toggleShowExamples(false)} text="Back"></Button>
                            </div>
                        </>
                    )}
                    {selectedUUIDs && insights && (
                        <div>
                            <div className="flex">
                                {selectedUUIDs.map((currUUID, currIndex) => {
                                    if (selectedUUIDs.length !== 1)
                                        return (
                                            <div key={currIndex} className="relative">
                                                {insightIndex == currIndex ? (
                                                    <div
                                                        className="absolute inset-0 flex justify-center items-center z-10 text-center border-2 border-success-background "
                                                        onClick={() => setInsightIndex(currIndex)}
                                                    ></div>
                                                ) : null}
                                                <div
                                                    className="flex"
                                                    onClick={() => {
                                                        setInsightIndex(currIndex);
                                                    }}
                                                >
                                                    <DetectionImage
                                                        key={currIndex}
                                                        image_path={
                                                            apiBaseUrl() +
                                                            '/insights/image/sentinel2/' +
                                                            currUUID +
                                                            '.png'
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        );
                                    else return null;
                                })}
                            </div>
                            <VesselInfo
                                selectedFeature={insights.features[insightIndex]}
                                setSelectedFeature={function (value: Feature | null): void {
                                    throw new Error('Function not implemented.');
                                }}
                                hideClearview
                                hideOrderReportImage
                                hideNavigation
                            ></VesselInfo>
                            <Button text=" Reset Clearview" onClick={() => resetClearview()}></Button>
                        </div>
                    )}
                </div>
            </div>
            <Popup
                isOpen={!!clearviewErrorMsg}
                tooglePopup={() => {
                    setClearviewErrorMsg(null);
                }}
            >
                {clearviewErrorMsg}
                <VText>Contact VAKE if you need help.</VText>
            </Popup>
            <CandidateList
                CandidatePositions={candidateInsights}
                clickedBoatIndex={clickedBoatIndex}
                setHoveredListBoatElement={setHoveredListBoatElement}
                isLoading={isCandidatesLoading}
                candidatesError={candidateErrorMsg}
                confidenceList={confidenceList}
                isAoiQueryed={aoi !== null}
                isDateQueryed={fromDate !== null || toDate !== null}
            />
            <ClearViewMap
                containerRef={containerRef}
                darkInsight={insights}
                candidateFeatures={candidateFeatures}
                resetMap={resetMap}
                drawMode={drawMode}
                polygon={polygon}
                setPolygon={setPolygon}
                setClickedboat={setClickedBoatIndex}
                hoveredListBoatElement={hoveredListBoatElement}
            />
        </>
    );
};

export default ClearViewReport;
